import {
  CommentQuestion,
  type EvaluatorValue,
  type ExecutionContext,
  type Hierarchical,
  type Item,
  isQuestion,
  OptionsValidator,
  type Question,
  Severity,
} from "anketa-core";

/**
 * Whether a question still needs an answer.
 *
 * anketa-core validators never check `required` (and `required` defaults to
 * true), so "missing" is decided here. Hidden, read-only and comment questions
 * never count.
 */
export const isAnswerMissing = (q: Question<EvaluatorValue>): boolean => {
  if (!q.required || q.readonly || !q.visible || q instanceof CommentQuestion) {
    return false;
  }
  const raw = q.fact?.raw as unknown;
  if (raw === undefined || raw === null) {
    return true;
  }
  if (typeof raw === "string") {
    return raw.trim() === "" || raw === OptionsValidator.options_choose;
  }
  if (Array.isArray(raw)) {
    return raw.length === 0;
  }
  return false;
};

/** Validation errors of a question, already rendered in the active locale. */
export const questionErrors = (
  q: Question<EvaluatorValue>,
  ctx: ExecutionContext,
): string[] => {
  // An unanswered question is "missing", not "invalid" - validators would
  // otherwise report e.g. a pattern mismatch on an empty string.
  if (!q.visible || q.readonly || isEmpty(q)) {
    return [];
  }
  const result = q.validate(ctx);
  const errors: string[] = [];
  for (const diag of result.diagnostics) {
    if (diag.severity === Severity.Error) {
      errors.push(ctx.i18nContext.render(diag.summary));
    }
  }
  return errors;
};

const isEmpty = (q: Question<EvaluatorValue>): boolean => {
  const raw = q.fact?.raw as unknown;
  return (
    raw === undefined ||
    raw === null ||
    (typeof raw === "string" && raw.trim() === "") ||
    (Array.isArray(raw) && raw.length === 0)
  );
};

export interface IssueCount {
  /** Visible, answerable questions in the subtree. */
  total: number;
  /** Of those, still unanswered. */
  missing: number;
  /** Of those, answered but failing validation. */
  invalid: number;
}

/** Walk the visible part of a subtree and count open / invalid answers. */
export const countIssues = (
  item: Item | Hierarchical,
  ctx: ExecutionContext,
): IssueCount => {
  const count: IssueCount = { total: 0, missing: 0, invalid: 0 };
  const walk = (node: Item | Hierarchical): void => {
    const children = (node as Item).children;
    if (!children) {
      return;
    }
    children.forEach((child: Hierarchical) => {
      if ((child as { visible?: boolean }).visible === false) {
        return;
      }
      if (isQuestion(child)) {
        const q = child as Question<EvaluatorValue>;
        if (!q.readonly && !(q instanceof CommentQuestion)) {
          count.total++;
          if (isAnswerMissing(q)) {
            count.missing++;
          } else if (questionErrors(q, ctx).length > 0) {
            count.invalid++;
          }
        }
      }
      // Questions can carry follow-up items of their own.
      walk(child);
    });
  };
  walk(item);
  return count;
};
